import { useState, useCallback, useLayoutEffect, useRef, useEffect } from 'react';
export function useTooltip() {
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState(null);
    const [tooltipContent, setTooltipContent] = useState(null);
    const [currentTargetElement, setCurrentTargetElement] = useState(null);
    const [currentTargetCoordinates, setCurrentTargetCoordinates] = useState(null);
    const [tooltipNode, setTooltipNode] = useState(null);
    const showTimerRef = useRef(null);
    const hideTimerRef = useRef(null);
    const tooltipCallbackRef = useCallback((node) => {
        setTooltipNode(node);
    }, []);
    const actuallyShowTooltip = useCallback((content, targetElement) => {
        setTooltipContent(content);
        setCurrentTargetElement(targetElement);
        setCurrentTargetCoordinates(null);
        setTooltipPosition(null);
        setIsTooltipVisible(true);
    }, []);
    const actuallyShowTooltipAtPosition = useCallback((content, coordinates) => {
        setTooltipContent(content);
        setCurrentTargetElement(null);
        setCurrentTargetCoordinates(coordinates);
        setTooltipPosition(null);
        setIsTooltipVisible(true);
    }, []);
    const actuallyHideTooltip = useCallback(() => {
        setTooltipPosition(prev => (prev ? { ...prev, opacity: 0 } : null));
        setTimeout(() => {
            setIsTooltipVisible(false);
            setTooltipContent(null);
            setCurrentTargetElement(null);
            setCurrentTargetCoordinates(null);
        }, 200);
    }, []);
    const showTooltip = useCallback((content, target) => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
        if (showTimerRef.current) {
            clearTimeout(showTimerRef.current);
        }
        if ('getBoundingClientRect' in target) {
            // HTMLElement target
            showTimerRef.current = setTimeout(() => {
                if (target.matches(':hover')) {
                    actuallyShowTooltip(content, target);
                }
                showTimerRef.current = null;
            }, 30);
        }
        else {
            // Coordinate target
            showTimerRef.current = setTimeout(() => {
                actuallyShowTooltipAtPosition(content, target);
                showTimerRef.current = null;
            }, 30);
        }
    }, [actuallyShowTooltip, actuallyShowTooltipAtPosition]);
    const hideTooltip = useCallback(() => {
        if (showTimerRef.current) {
            clearTimeout(showTimerRef.current);
            showTimerRef.current = null;
        }
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
        }
        hideTimerRef.current = setTimeout(() => {
            actuallyHideTooltip();
            hideTimerRef.current = null;
        }, 100);
    }, [actuallyHideTooltip]);
    useEffect(() => {
        return () => {
            if (showTimerRef.current)
                clearTimeout(showTimerRef.current);
            if (hideTimerRef.current)
                clearTimeout(hideTimerRef.current);
        };
    }, []);
    useLayoutEffect(() => {
        if (isTooltipVisible &&
            tooltipNode &&
            (currentTargetElement || currentTargetCoordinates) &&
            tooltipContent) {
            const tooltipWidth = tooltipNode.offsetWidth;
            const tooltipHeight = tooltipNode.offsetHeight;
            if (tooltipWidth > 0 && tooltipHeight > 0) {
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const scrollX = window.scrollX;
                const scrollY = window.scrollY;
                let top, left;
                const margin = 8;
                let placement = 'top';
                let targetRect;
                if (currentTargetElement) {
                    // Element-based positioning
                    targetRect = currentTargetElement.getBoundingClientRect();
                }
                else if (currentTargetCoordinates) {
                    // Coordinate-based positioning
                    targetRect = {
                        top: currentTargetCoordinates.y,
                        bottom: currentTargetCoordinates.y,
                        left: currentTargetCoordinates.x,
                        right: currentTargetCoordinates.x,
                        width: 0,
                        height: 0,
                        x: currentTargetCoordinates.x,
                        y: currentTargetCoordinates.y,
                    };
                }
                else {
                    return; // Should not happen, but safety check
                }
                top = targetRect.top + scrollY - tooltipHeight - margin;
                if (top - scrollY < margin) {
                    placement = 'bottom';
                    top = targetRect.bottom + scrollY + margin;
                    if (top - scrollY + tooltipHeight + margin > viewportHeight) {
                        if (targetRect.top + targetRect.height / 2 < viewportHeight / 2) {
                            placement = 'bottom_overflow';
                        }
                        else {
                            placement = 'top_overflow';
                            const originalTopAttempt = targetRect.top + scrollY - tooltipHeight - margin;
                            if (originalTopAttempt - scrollY < margin)
                                top = scrollY + margin;
                            else
                                top = originalTopAttempt;
                        }
                    }
                }
                left = targetRect.left + scrollX + targetRect.width / 2 - tooltipWidth / 2;
                if (left + tooltipWidth + margin > viewportWidth + scrollX) {
                    left = viewportWidth + scrollX - tooltipWidth - margin;
                }
                if (left < scrollX + margin) {
                    left = scrollX + margin;
                }
                const finalPosition = { top, left, opacity: 1, placement };
                if (!tooltipPosition ||
                    tooltipPosition.top !== finalPosition.top ||
                    tooltipPosition.left !== finalPosition.left ||
                    tooltipPosition.opacity !== finalPosition.opacity ||
                    tooltipPosition.placement !== finalPosition.placement) {
                    setTooltipPosition(finalPosition);
                }
            }
            else {
                const hiddenState = { top: 0, left: 0, opacity: 0, placement: 'top' };
                if (!tooltipPosition ||
                    tooltipPosition.top !== hiddenState.top ||
                    tooltipPosition.left !== hiddenState.left ||
                    tooltipPosition.opacity !== hiddenState.opacity ||
                    tooltipPosition.placement !== hiddenState.placement) {
                    setTooltipPosition(hiddenState);
                }
            }
        }
        else if (!isTooltipVisible) {
            if (tooltipPosition !== null) {
                setTooltipPosition(null);
            }
        }
    }, [
        isTooltipVisible,
        tooltipContent,
        currentTargetElement,
        currentTargetCoordinates,
        tooltipNode,
        tooltipPosition,
    ]);
    return {
        isTooltipVisible,
        tooltipPosition,
        tooltipContent,
        showTooltip,
        hideTooltip,
        tooltipRef: tooltipCallbackRef,
    };
}
//# sourceMappingURL=useTooltip.js.map